import React, { useState } from 'react';
import Guru from '@svg/guru';
import styled from 'styled-components';

import { Button, LoadingButton, Modal } from '@common/index';
import { Stack, alpha } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateLoaderSchedule } from '@requests/loaders';
import { useParams } from 'react-router-dom';
import { State } from '@store/index';
import { useSelector } from 'react-redux';
import { useTheme } from '../../../../styles';
import { CronError } from 'react-js-cron';
import { CronSchedule } from '@common/CronSchedule';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  & .MuiInputBase-input {
    padding: 10px 14px;
  }
`;

interface Props {
  open: string | null;
  page: number;
  setOpen: React.Dispatch<React.SetStateAction<string | null>>;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  reset: () => void;
  configuration: {
    intervalValue: string;
    setIntervalValue: React.Dispatch<React.SetStateAction<string>>;
    unit: string;
    setUnit: React.Dispatch<React.SetStateAction<string>>;
    loader: boolean;
  };
}

export const GuruSchedule: React.FC<Props> = ({ open, page, setPage, configuration, reset }) => {
  const queryClient = useQueryClient();

  const username = useSelector((state: State) => state.auth.username);
  const theme = useTheme();

  const { service_id, bucketName } = useParams();
  const { intervalValue, setIntervalValue, unit } = configuration;

  const [error, onError] = useState<CronError>();

  const { mutateAsync: update, status: updateStatus } = useMutation({
    mutationFn: updateLoaderSchedule,
    onSuccess: (): void => {
      queryClient.invalidateQueries({ queryKey: ['globalFiles', 'bucket-loader'] });
      reset();
    },
  });

  const updateSchedule = () => {
    update({
      service_id: service_id as string,
      bucket_name: bucketName as string,
      intervalValue,
      unit,
      user_id: username as string,
    });
  };

  const body = (
    <Container>
      <CronSchedule value={intervalValue} setValue={setIntervalValue} error={error} onError={onError} />
    </Container>
  );

  const loaderActions = (
    <Stack spacing={1} direction='row'>
      <Button onClick={reset}>Back</Button>
      <LoadingButton
        minWidth='145px'
        minHeight='32px'
        loading={updateStatus === 'pending'}
        disabled={!intervalValue}
        customColor='primary'
        onClick={updateSchedule}
      >
        Update Schedule
      </LoadingButton>
    </Stack>
  );

  const actions = (
    <Stack spacing={1} direction='row'>
      <Button onClick={reset}>Back</Button>
      <Button disabled={!intervalValue} customColor='primary' onClick={() => setPage(2)}>
        Set Schedule
      </Button>
    </Stack>
  );

  return (
    <Modal
      open={(open === 'guru' && page === 1) || (open === 'guru' && configuration?.loader)}
      setOpen={reset}
      page={configuration?.loader ? undefined : page + 1}
      total_pages={configuration?.loader ? undefined : 4}
      info='Specify how often to refresh the content in this bucket.'
      background={alpha(theme.text.standard, 0.06)}
      header='Guru'
      icon={<Guru height={30} color='#000' />}
      body={body}
      actions={configuration?.loader ? loaderActions : actions}
    />
  );
};
